import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
} from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import RestoreIcon from '@material-ui/icons/Restore';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useSnackbar } from 'notistack';

import { getListAllAPI, getListAPI } from '../../services';
import { CATEGORIAS_ENDERECO_EXPOSICAO, STATUS_ATIVO } from '../../utils/constants';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: 'black',
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6" style={{ fontWeight: 'bold' }}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    height: '100%',
    padding: 0,
    paddingBottom: theme.spacing(5),
    overflow: 'hidden',
  },
  sidebarEsquerda: {
    width: '260px',
    flexShrink: 0,
    borderRight: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  sidebarDireita: {
    width: '260px',
    flexShrink: 0,
    borderLeft: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  cabecalhoFixo: {
    flexShrink: 0,
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  listaScrollavel: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
  },
  dicaSidebar: {
    display: 'block',
    marginBottom: theme.spacing(1.5),
  },
  divisorSidebar: {
    marginBottom: 0,
  },
  cardMapa: {
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    padding: '8px 10px',
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
  },
  cardMapaSelecionado: {
    borderColor: '#1e88e5',
    backgroundColor: '#e3f2fd',
  },
  andaresSeletor: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: '1px solid #e0e0e0',
    flexShrink: 0,
  },
  chipAndar: {
    borderRadius: '16px',
    border: '1px solid #bdbdbd',
    padding: '4px 12px',
    fontSize: '0.8125rem',
    cursor: 'pointer',
    userSelect: 'none',
  },
  chipAndarSelecionado: {
    borderColor: '#1e88e5',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    fontWeight: 'bold',
  },
  areaCentral: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  // O wrapper não rola (overflow: hidden) e serve só de âncora pros controles
  // de zoom flutuantes, que assim ficam sempre visíveis no canto — se o zoom
  // estivesse ligado ao elemento que rola, os botões sumiriam de vista ao
  // arrastar o mapa.
  areaMapaWrapper: {
    position: 'relative',
    flex: 1,
    overflow: 'hidden',
  },
  areaMapa: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    // "safe center" evita o problema clássico de flexbox onde um conteúdo
    // centralizado maior que o container fica com uma parte inacessível
    // mesmo rolando — sem isso, dar zoom além do tamanho da tela cortaria
    // pedaços do grid que a rolagem não alcançaria.
    alignItems: 'safe center',
    justifyContent: 'safe center',
  },
  areaMapaVazia: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  controlesZoom: {
    position: 'absolute',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    padding: '2px',
    zIndex: 1,
  },
  labelZoom: {
    minWidth: '40px',
    textAlign: 'center',
  },
  grid: {
    display: 'grid',
  },
  celula: {
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    userSelect: 'none',
  },
  celulaOcupada: {
    borderStyle: 'solid',
  },
  // Destaque de produto (busca) é um "anel" por cima da cor da categoria, não
  // uma cor de fundo própria — evita confundir com a categoria "Ilha", que
  // também é verde.
  celulaDestaqueProduto: {
    borderStyle: 'solid',
    borderWidth: '3px',
    borderColor: '#0d47a1',
    boxShadow: '0 0 0 3px rgba(13, 71, 161, 0.45)',
    zIndex: 1,
  },
  legendaCategoriaEndereco: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  legendaCategoriaEnderecoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  legendaCategoriaEnderecoContagem: {
    marginLeft: '20px',
  },
  legendaCategoriaEnderecoCor: {
    width: '14px',
    height: '14px',
    borderRadius: '3px',
    border: '1px solid rgba(0, 0, 0, 0.2)',
    flexShrink: 0,
  },
  legendaDestaqueCor: {
    backgroundColor: '#fff',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: '#0d47a1',
    boxShadow: '0 0 0 2px rgba(13, 71, 161, 0.45)',
  },
  celulaConteudo: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    overflow: 'hidden',
    padding: '2px',
    boxSizing: 'border-box',
  },
}));

function getChaveCelula(x, y) {
  return `${x}-${y}`;
}

const OPCOES_ANDAR = [
  { valor: 1, label: 'Térreo' },
  { valor: 2, label: '1º Aéreo' },
  { valor: 3, label: '2º Aéreo' },
];

const DialogoEscolherMapaEAndar = forwardRef((props, ref) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [carregandoMapas, setCarregandoMapas] = useState(false);
  const [carregandoMapa, setCarregandoMapa] = useState(false);
  const [mapas, setMapas] = useState([]);
  const [mapaSelecionado, setMapaSelecionado] = useState(null);
  const [andarSelecionado, setAndarSelecionado] = useState(null);

  const [enderecosBrutos, setEnderecosBrutos] = useState([]);
  const [largura, setLargura] = useState(10);
  const [altura, setAltura] = useState(10);

  const [produtoFiltro, setProdutoFiltro] = useState(null);
  const [inputValueProduto, setInputValueProduto] = useState('');
  const [valueDebounceProduto, setValueDebounceProduto] = useState('');
  const [optionsProduto, setOptionsProduto] = useState([]);

  useEffect(() => {
    const timer = setTimeout(
      () => setValueDebounceProduto(inputValueProduto),
      500,
    );
    return () => clearTimeout(timer);
  }, [inputValueProduto]);

  useEffect(() => {
    if (!andarSelecionado) return undefined;

    let active = true;
    (async () => {
      const { data } = await getListAPI(
        'produtos_search_filter',
        ['id', 'asc'],
        [1, 10],
        {
          nome: valueDebounceProduto,
          status: STATUS_ATIVO,
        },
        [],
      );
      if (active) setOptionsProduto(data);
    })();

    return () => {
      active = false;
    };
  }, [valueDebounceProduto, andarSelecionado]);

  const areaMapaRef = useRef(null);
  const [tamanhoBase, setTamanhoBase] = useState(20);
  const [zoom, setZoom] = useState(1.75);

  const GAP_CELULA = 2;
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 5;
  const ZOOM_PASSO = 0.25;

  const tamanhoCelula = tamanhoBase * zoom;

  function handleZoomIn() {
    setZoom((prev) => Math.min(ZOOM_MAX, Math.round((prev + ZOOM_PASSO) * 100) / 100));
  }

  function handleZoomOut() {
    setZoom((prev) => Math.max(ZOOM_MIN, Math.round((prev - ZOOM_PASSO) * 100) / 100));
  }

  function handleZoomReset() {
    setZoom(1);
  }

  useEffect(() => {
    const elemento = areaMapaRef.current;
    if (!elemento || !mapaSelecionado) return undefined;

    function recalcular() {
      const { clientWidth, clientHeight } = elemento;
      const larguraDisponivel = clientWidth - GAP_CELULA * (largura - 1);
      const alturaDisponivel = clientHeight - GAP_CELULA * (altura - 1);
      const tamanho = Math.floor(
        Math.min(larguraDisponivel / largura, alturaDisponivel / altura),
      );
      setTamanhoBase(Math.max(tamanho, 4));
    }

    recalcular();

    const observer = new ResizeObserver(recalcular);
    observer.observe(elemento);
    return () => observer.disconnect();
  }, [largura, altura, carregandoMapa, mapaSelecionado]);

  async function getMapas() {
    setCarregandoMapas(true);
    try {
      const dataMapas = await getListAllAPI(
        'mapas_loja',
        ['nome', 'asc'],
        { status: STATUS_ATIVO },
        [],
      );
      setMapas(dataMapas.data);

      const mapaPrincipal = dataMapas.data.find((m) => m.nome === 'Mapa Principal');
      if (mapaPrincipal) handleSelecionarMapa(mapaPrincipal);
    } catch (erros) {
      enqueueSnackbar(`${erros}`, { variant: 'error' });
    } finally {
      setCarregandoMapas(false);
    }
  }

  useImperativeHandle(ref, () => ({
    handleOpen() {
      setOpen(true);
      setMapaSelecionado(null);
      setAndarSelecionado(1);
      setZoom(1.75);
      getMapas();
    },
  }));

  function handleClose() {
    setOpen(false);
  }

  async function handleSelecionarMapa(mapa) {
    setMapaSelecionado(mapa);
    setAndarSelecionado(1);
    setProdutoFiltro(null);
    setInputValueProduto('');
    setZoom(1.75);
    setCarregandoMapa(true);
    try {
      const dataEnderecos = await getListAllAPI(
        'enderecos_exposicao_mercadorias',
        ['codigo', 'asc'],
        { status: STATUS_ATIVO, mapa_loja_id: mapa.id },
        ['produto', 'andaresProdutos'],
      );

      setLargura(mapa.largura);
      setAltura(mapa.altura);
      setEnderecosBrutos(dataEnderecos.data);
    } catch (erros) {
      enqueueSnackbar(`${erros}`, { variant: 'error' });
    } finally {
      setCarregandoMapa(false);
    }
  }

  // Quando tem andar selecionado, mostra todo endereço que TEM aquele andar
  // (quantidade_andares >= andarSelecionado), tenha produto ativo nele ou
  // não — antes só aparecia se já tivesse produto, escondendo endereços com
  // aquele andar vazio.
  const enderecos = andarSelecionado
    ? enderecosBrutos.filter(
        (e) => (e.quantidade_andares || 1) >= andarSelecionado,
      )
    : enderecosBrutos;

  function correspondeProdutoFiltro(endereco) {
    if (!produtoFiltro) return false;
    return (endereco.andaresProdutos || []).some(
      (ap) =>
        ap.andar === andarSelecionado &&
        ap.status === STATUS_ATIVO &&
        ap.produto_id === produtoFiltro.id,
    );
  }

  const mapaEnderecosPorCelula = {};
  enderecos.forEach((e) => {
    if (e.posicao_x !== null && e.posicao_x !== undefined && e.posicao_y !== null && e.posicao_y !== undefined) {
      mapaEnderecosPorCelula[getChaveCelula(e.posicao_x, e.posicao_y)] = e;
    }
  });

  const corPorCategoriaEndereco = {};
  const corBordaPorCategoriaEndereco = {};
  CATEGORIAS_ENDERECO_EXPOSICAO.forEach((c) => {
    corPorCategoriaEndereco[c.categoria] = c.cor;
    corBordaPorCategoriaEndereco[c.categoria] = c.corBorda;
  });

  function possuiProdutoAtivo(endereco) {
    if (andarSelecionado === 1 && endereco.produto) return true;
    return (endereco.andaresProdutos || []).some(
      (ap) => ap.andar === andarSelecionado && ap.status === STATUS_ATIVO,
    );
  }

  const contagemPorCategoria = {};
  CATEGORIAS_ENDERECO_EXPOSICAO.forEach((c) => {
    // Só entram na contagem os endereços já posicionados neste mapa.
    const doCategoriaNoMapa = enderecos.filter(
      (e) =>
        e.categoria === c.categoria &&
        e.posicao_x !== null &&
        e.posicao_x !== undefined &&
        e.posicao_y !== null &&
        e.posicao_y !== undefined,
    );
    const preenchidos = doCategoriaNoMapa.filter(possuiProdutoAtivo).length;
    contagemPorCategoria[c.categoria] = {
      preenchidos,
      disponiveis: doCategoriaNoMapa.length - preenchidos,
    };
  });

  const celulas = [];
  if (mapaSelecionado) {
    for (let y = 0; y < altura; y += 1) {
      for (let x = 0; x < largura; x += 1) {
        celulas.push({
          x,
          y,
          endereco: mapaEnderecosPorCelula[getChaveCelula(x, y)] || null,
        });
      }
    }
  }

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="dialogo-escolher-mapa-e-andar"
      open={open}
      fullScreen
    >
      <DialogTitle onClose={handleClose}>
        {mapaSelecionado ? `Visualizar Mapa - ${mapaSelecionado.nome}` : 'Visualizar Mapa'}
      </DialogTitle>
      <Divider />
      <MuiDialogContent className={classes.content}>
        <div className={classes.sidebarEsquerda}>
          <div className={classes.cabecalhoFixo}>
            <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
              Mapas
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              className={classes.dicaSidebar}
            >
              Escolha um mapa pra visualizar
            </Typography>
            <Divider className={classes.divisorSidebar} />
          </div>
          <div className={classes.listaScrollavel}>
            {carregandoMapas ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                <CircularProgress size={24} />
              </div>
            ) : (
              mapas.map((mapa) => (
                <div
                  key={mapa.id}
                  className={`${classes.cardMapa} ${
                    mapaSelecionado && mapaSelecionado.id === mapa.id
                      ? classes.cardMapaSelecionado
                      : ''
                  }`}
                  onClick={() => handleSelecionarMapa(mapa)}
                >
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {mapa.nome}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {`${mapa.largura} x ${mapa.altura}`}
                  </Typography>
                </div>
              ))
            )}
          </div>
        </div>

        <div className={classes.areaCentral}>
          {mapaSelecionado ? (
            <div className={classes.andaresSeletor}>
              {OPCOES_ANDAR.map((opcao) => (
                <div
                  key={String(opcao.valor)}
                  className={`${classes.chipAndar} ${
                    andarSelecionado === opcao.valor ? classes.chipAndarSelecionado : ''
                  }`}
                  onClick={() => setAndarSelecionado(opcao.valor)}
                >
                  {opcao.label}
                </div>
              ))}
            </div>
          ) : null}

          {!mapaSelecionado ? (
            <div className={classes.areaMapaVazia}>
              <Typography variant="body2" color="textSecondary">
                Escolha um mapa na lista ao lado
              </Typography>
            </div>
          ) : carregandoMapa ? (
            <div className={classes.areaMapaVazia}>
              <CircularProgress />
            </div>
          ) : (
            <div className={classes.areaMapaWrapper} ref={areaMapaRef}>
              <div className={classes.areaMapa}>
                <div
                  className={classes.grid}
                  style={{
                    gridTemplateColumns: `repeat(${largura}, ${tamanhoCelula}px)`,
                    gridTemplateRows: `repeat(${altura}, ${tamanhoCelula}px)`,
                    gap: `${GAP_CELULA}px`,
                  }}
                >
                  {celulas.map(({ x, y, endereco }) => {
                    const destaque = endereco
                      ? correspondeProdutoFiltro(endereco)
                      : false;
                    return (
                    <div
                      key={getChaveCelula(x, y)}
                      className={`${classes.celula} ${
                        endereco ? classes.celulaOcupada : ''
                      } ${destaque ? classes.celulaDestaqueProduto : ''}`}
                      style={
                        endereco
                          ? {
                              backgroundColor: corPorCategoriaEndereco[endereco.categoria],
                              borderColor: destaque
                                ? undefined
                                : corBordaPorCategoriaEndereco[endereco.categoria],
                            }
                          : undefined
                      }
                    >
                      {endereco ? (
                        <div className={classes.celulaConteudo}>
                          <Typography
                            style={{
                              fontWeight: 'bold',
                              fontSize: `${Math.max(tamanhoCelula * 0.3, 9)}px`,
                              lineHeight: 1.1,
                            }}
                          >
                            {endereco.codigo}
                          </Typography>
                        </div>
                      ) : null}
                    </div>
                    );
                  })}
                </div>
              </div>
              <Paper className={classes.controlesZoom} elevation={3}>
                <IconButton size="small" onClick={handleZoomOut}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" className={classes.labelZoom}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <IconButton size="small" onClick={handleZoomIn}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleZoomReset}
                  title="Ajustar à tela"
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </Paper>
            </div>
          )}
        </div>

        {mapaSelecionado && andarSelecionado ? (
          <div className={classes.sidebarDireita}>
            <div className={classes.cabecalhoFixo}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                Filtrar por produto
              </Typography>
              <Autocomplete
                value={produtoFiltro}
                onChange={(event, newValue) => setProdutoFiltro(newValue)}
                inputValue={inputValueProduto}
                onInputChange={(event, newInputValue) =>
                  setInputValueProduto(newInputValue)
                }
                getOptionSelected={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.nome || ''}
                options={optionsProduto}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    margin="dense"
                    label="Produto"
                    fullWidth
                  />
                )}
              />
              <Divider className={classes.divisorSidebar} style={{ marginTop: 12 }} />
              <div className={classes.legendaCategoriaEndereco}>
                {CATEGORIAS_ENDERECO_EXPOSICAO.map((c) => (
                  <div key={c.categoria}>
                    <div className={classes.legendaCategoriaEnderecoItem}>
                      <div
                        className={classes.legendaCategoriaEnderecoCor}
                        style={{ backgroundColor: c.cor }}
                      />
                      <Typography variant="caption">{c.label}</Typography>
                    </div>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      className={classes.legendaCategoriaEnderecoContagem}
                    >
                      {`${contagemPorCategoria[c.categoria].preenchidos} preenchido${
                        contagemPorCategoria[c.categoria].preenchidos === 1 ? '' : 's'
                      } · ${contagemPorCategoria[c.categoria].disponiveis} disponível${
                        contagemPorCategoria[c.categoria].disponiveis === 1 ? '' : 'is'
                      }`}
                    </Typography>
                  </div>
                ))}
                <div className={classes.legendaCategoriaEnderecoItem}>
                  <div
                    className={`${classes.legendaCategoriaEnderecoCor} ${classes.legendaDestaqueCor}`}
                  />
                  <Typography variant="caption">Produto selecionado</Typography>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </MuiDialogContent>
    </Dialog>
  );
});

export default DialogoEscolherMapaEAndar;
